import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Partner } from 'src/app/shared/models/partner'
import { Show } from 'src/app/shared/models/show'
import { environment } from 'src/environments/environment';
import { PartnerGroup } from '../models/partner-group';
import { PartnerGroupMember } from '../models/partner-group-member';
import { query } from 'express';
import { PartnerPro } from '../models/partner-pro';
import { UserProDataSet } from '../models/user-pro-data-set';
import { BulkShowRequest, BulkShowResponse } from '../models/bulk-show';

@Injectable({
  providedIn: 'root'
})
export class PartnerService {

  private _hasClickedGetPros = new BehaviorSubject<boolean>(false);
  hasClickedGetPros$ = this._hasClickedGetPros.asObservable();
  
  baseUrl = environment.apiURL + '/api/v1/admin/partner/';
  showsUrl = environment.apiURL + '/api/v1/shows/';
  permissionsUrl = environment.apiURL + '/api/v1/admin/groups/';
  userProsUrl = environment.apiURL + '/api/v1/user/pros/';
  usersUrl = environment.apiURL + '/api/v1/admin/users/';

  /**
   * Represents the columns used on the displayed tables
   */
  public masterColumns = [
    { value: '#', name: 'row number' },
    { value: 'YEAR', name: 'year' },
    { value: 'QUARTER', name: 'quarter' },
    { value: 'DISTRIBUTION YEAR', name: 'distribution year' },
    { value: 'DISTRIBUTION QUARTER', name: 'distribution quarter' },
    { value: 'STATEMENT RECIPIENT ID', name: 'statement recipient id' },
    { value: 'STATEMENT RECIPIENT NAME', name: 'statement recipient name' },
    { value: 'PARTY ID', name: 'party id' },
    { value: 'PARTY NAME', name: 'party name' },
    { value: 'LEGAL EARNER PARTY ID', name: 'legal earner party id' },
    { value: 'LEGAL EARNER PARTY NAME', name: 'legal earner party name' },
    { value: 'PERFORMANCE SOURCE', name: 'performance source/broadcast medium' },
    { value: 'MUSIC USER GENRE', name: 'music user genre' },
    { value: 'MUSIC USER', name: 'music user' },
    { value: 'NETWORK SERVICE', name: 'network service' },
    { value: 'PERFORMANCE START DATE', name: 'performance start date' },
    { value: 'PERFORMANCE END DATE', name: 'performance end date' },
    { value: 'SURVEY TYPE', name: 'survey type' },
    { value: 'DAY PART CODE', name: 'day part code' },
    { value: 'SERIES OR FILM/ATTRACTION', name: 'series or film/attraction' },
    { value: 'PROGRAM/EPISODE NAME', name: 'program/episode name' },
    { value: 'WORK ID', name: 'work id' },
    { value: 'WORK TITLE', name: 'work title' },
    { value: 'CA%', name: 'ca%' },
    { value: 'CLASSIFICATION CODE', name: 'classification code' },
    { value: 'NUMBER OF PLAYS', name: 'number of plays' },
    { value: 'PERFORMANCE TYPE', name: 'performance type (usage)' },
    { value: 'DURATION', name: 'duration' },
    { value: 'PERFORMING ARTIST', name: 'performing artist' },
    { value: 'COMPOSER NAME', name: 'composer name' },
    { value: 'EE SHARE', name: 'ee share' },
    { value: 'CREDITS', name: 'credits' },
    { value: 'USD', name: 'usd' },
    { value: '%USD', name: 'adj_usd' },
    { value: 'PREMIUM CREDITS', name: 'premium credits' },
    { value: 'PREMIUM ROYALTY', name: 'premium dollars' },
    { value: 'ADJUSTMENT INDICATOR', name: 'adjustment indicator' },
    { value: 'ADJUSTMENT REASON CODE', name: 'adjustment reason code' },
    { value: 'ORIGINAL DISTRIBUTION DATE', name: 'original distribution date' },
    { value: 'ROLE TYPE', name: 'role type' },
    { value: 'TYPE OF RIGHT', name: 'type of right' },
    { value: 'TERRITORY', name: 'territory' },
    { value: 'LICENSOR', name: 'licensor' },
    { value: 'PROGRAM CODE', name: 'program code' },
    { value: 'PERFORMANCE QUARTER', name: 'performance quarter' },
    { value: 'COUNTRY NAME', name: 'country name' },
    { value: 'FILE TYPE', name: 'file type' },
    { value: 'LICENSOR CODE', name: 'licensor code' },
    { value: 'REVENUE CLASS CODE', name: 'revenue class code' },
    { value: 'REVENUE CLASS DESCRIPTION', name: 'revenue class description' },
    { value: 'ADJUSTMENT DISTRIBUTION DATE', name: 'adjustment distribution date' },
    { value: 'CURRENT ACTIVITY AMOUNT', name: 'current activity amt' },
    { value: 'HIT SONG BONUS', name: 'hit song or tv net super usage bonus' },
    { value: 'STANDARDS BONUS', name: 'standards or tv net theme bonus' },
    { value: 'FOREIGN SOCIETY ADJUSTMENT', name: 'foreign society adjustment' },
    { value: 'PERFORMANCE DATE', name: 'perf date' },
    { value: 'W OR P', name: 'w or p' },
    { value: 'SHOW NAME', name: 'show name' },
    { value: 'PAYMENT TYPE', name: 'payment type' },
    { value: 'CHART', name: 'chart' },
    { value: 'PAY NUMBER', name: 'pay #' },
    { value: 'SYND', name: 'synd' },
    { value: 'STRIP', name: 'strip' },
    { value: 'PERFORMANCE', name: 'performance' },
    { value: 'OCCUR', name: 'occur' },
    { value: 'SOCIETY ABBRV', name: 'society abbrv' },
    { value: 'SOCIETY COUNTRY', name: 'society country' }
  ];



  //Sub view controls for data-parnters view
  // private showPartnersMgmtSource = new ReplaySubject<boolean>(1);
  // showPartnersMgmt$ = this.showPartnersMgmtSource.asObservable();

  // private showPartnerGroupsMgmtSource = new ReplaySubject<boolean>(1);
  // showPartnerGroupsMgmt$ = this.showPartnerGroupsMgmtSource.asObservable();

  setHasClickedGetPros(value: boolean) {
    this._hasClickedGetPros.next(value);
  }

  getCurrentValue(): boolean {
    return this._hasClickedGetPros.getValue();
  }

  private showProDetailsSource = new ReplaySubject<boolean>(1);
  showProDetails$ = this.showProDetailsSource.asObservable();

  private showShowsSource = new ReplaySubject<boolean>(1);
  showShows$ = this.showShowsSource.asObservable();


  private partnersSource = new ReplaySubject<Partner[]>(1);
  partners$ = this.partnersSource.asObservable();

  private partnerGroupsSource = new ReplaySubject<PartnerGroup[]>(1);
  partnerGroups$ = this.partnerGroupsSource.asObservable();


  //Selected partner for details view
  private selectedPartnerSource = new ReplaySubject<Partner>(1);
  selectedPartner$ = this.selectedPartnerSource.asObservable();

  //SELECT OPTIONS FOR data/partners/details pro view
  private proTypeSource = new ReplaySubject<string>(1);
  proType$ = this.proTypeSource.asObservable();

  private proYearSource = new ReplaySubject<number>(1);
  proYear$ = this.proYearSource.asObservable();

  private proQuarterSource = new ReplaySubject<string>(1);
  proQuarter$ = this.proQuarterSource.asObservable();

  private proStatusSource = new ReplaySubject<string>(1);
  proStatus$ = this.proStatusSource.asObservable();


  //all shows in system
  private allShowsSource = new ReplaySubject<Show[]>(1);
  allShows$ = this.allShowsSource.asObservable();


  //user types
  private userTypesSource = new ReplaySubject<any[]>(1);
  userTypes$ = this.userTypesSource.asObservable();


  //non super admin users
  private nonSuperAdminUsersSource = new ReplaySubject<any[]>(1);
  nonSuperAdminUsers$ = this.nonSuperAdminUsersSource.asObservable();


  //existing user data sets
  private userDataSetsSource = new ReplaySubject<any[]>(1);
  userDataSets$ = this.userDataSetsSource.asObservable();


  //stores the url for the next batch of shows when calling get all shows
  private moreShowsUrlSource = new ReplaySubject<string>(1);
  moreShowsUrl$ = this.moreShowsUrlSource.asObservable();


  //used with custom pagination
  private tablePageSource = new ReplaySubject<number>(1);
  tablePage$ = this.tablePageSource.asObservable();

  private tablePageSizeSource = new ReplaySubject<number>(1);
  tablePageSize$ = this.tablePageSizeSource.asObservable();


  //current user selected data set
  private currentUserSelectedDataSetSource = new ReplaySubject<UserProDataSet>(1);
  currentUserSelectedDataSet$ = this.currentUserSelectedDataSetSource.asObservable();

  // data management modal subject
  private modalClosedSubject = new Subject<void>();
  modalClosed$ = this.modalClosedSubject.asObservable();

  // added these two to prevent exponential looping in pro component tables
  fetchingPros = false;
  fetchingUserDataSets = false;

  selectedPartnerId: any;
  exportedProIDs: any = [];

  constructor(private http: HttpClient) {

    this.getPartners().subscribe(x => {
      this.setPartners(x);
    });

    this.getPartnerGroups().subscribe(x => {
      this.setPartnerGroups(x);
    })
  }

  // setShowPartnersMgmt(value: boolean) {
  //   this.showPartnersMgmtSource.next(value);
  // }

  // setShowPartnerGroupsMgmt(value: boolean) {
  //   this.showPartnerGroupsMgmtSource.next(value);
  // }

  setShowProDetails(value: boolean) {
    this.showProDetailsSource.next(value);
  }

  setShowShows(value: boolean) {
    this.showShowsSource.next(value);
  }

  setPartners(partners: Partner[]) {

    //move mibe to the top of the list
    const mibeMusicIndex = partners.findIndex(partner => partner.name === 'Mibe Music');
    if (mibeMusicIndex !== -1) {
      const [mibeMusicPartner] = partners.splice(mibeMusicIndex, 1);
      partners.unshift(mibeMusicPartner);
    }

    this.partnersSource.next(partners);
  }

  setPartnerGroups(groups: PartnerGroup[]) {
    this.partnerGroupsSource.next(groups);
  }

  setSelectedPartner(partner: Partner) {
    this.selectedPartnerSource.next(partner);
  }


  //SELECT OPTIONS FOR data/partners/details pro view
  setProType(proType: string) {
    this.proTypeSource.next(proType);
    //this.isImportTypeSet = true;
  }

  setProYear(proYear: number) {
    this.proYearSource.next(proYear);
    //this.isImportYearSet = true;
  }

  setProQuarter(proQuarter: string) {
    this.proQuarterSource.next(proQuarter);
    //this.isImportQuarterSet = true;
  }

  setProStatus(status: string) {
    this.proStatusSource.next(status);
  }


  setAllShows(shows: Show[]) {
    // //set alphabetically
    // this.allShowsSource.next(shows.sort((a, b) => {
    //   if (a.name < b.name) {
    //     return -1;
    //   }
    //   if (a.name > b.name) {
    //     return 1;
    //   }
    //   return 0;
    // }));

    //turned off sorting since pagination does not
    //pull shows in alphabetical order - may need
    //to create a new method for pulling shows on the api
    this.allShowsSource.next(shows);
  }


  setUserTypes(types: any[]) {
    this.userTypesSource.next(types);
  }


  setNonSuperAdminUsers(users: any[]) {
    this.nonSuperAdminUsersSource.next(users);
  }

  setUserDataSets(values: any[]) {
    this.userDataSetsSource.next(values);
  }

  setMoreShowsUrl(url: string) {
    this.moreShowsUrlSource.next(url);
  }

  setTablePage(page: number) {
    this.tablePageSource.next(page);
  }

  setTablePageSize(pageSize: number) {
    this.tablePageSizeSource.next(pageSize);
  }

  setCurrentUserSelectedDataSet(dataSet: UserProDataSet | null) {
    this.currentUserSelectedDataSetSource.next(dataSet);
  }



  //HTTP REQUESTS - START

  addNewPartner(partner: Partner, userId: number) {

    const formData = new FormData();
    formData.append('name', partner.name);
    formData.append('earningsYTD', partner.earningsYTD.toString());
    formData.append('proStatus', partner.proStatus);
    formData.append('userId', userId.toString());

    return this.http.post<Partner>(this.baseUrl + 'post-partner', formData);

  }

  getPartners() {
    return this.http.get<Partner[]>(this.baseUrl + 'get-partners').pipe(
      map((response: any[]) => {
        return response.map(item => Partner.mapProperties(item));
      })
    );
  }

  getPartner(id: number) {
    const params = new HttpParams().set('id', id.toString());
    return this.http.get<Partner>(this.baseUrl + 'get-partners', { params }).pipe(
      map((response: any) => {
        return Partner.mapProperties(response);
      })
    );
  }

  /**
 * Create or update a Partner
 * This can be used when added a show for the partner
 * @param partner
 */
  updatePartner(partner: Partner): Observable<Partner> {
    const url = `${this.baseUrl}put-partner`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.put<Partner>(url, partner, httpOptions).pipe(
      map((response: any) => {
        return Partner.mapProperties(response);
      })
    );
  }

  addNewPartnerGroup(group: PartnerGroup, userId: number) {

    const formData = new FormData();
    formData.append('name', group.name);
    formData.append('userId', userId.toString());

    return this.http.post<PartnerGroup>(this.baseUrl + 'post-partner-group', formData);
  }

  getPartnerGroups() {
    return this.http.get<PartnerGroup[]>(this.baseUrl + 'get-partner-groups').pipe(
      map((response: any[]) => {
        return response.map(item => PartnerGroup.mapProperties(item));
      })
    )
  }

  updatePartnerGroup(partnerGroup: PartnerGroup) {
    const url = `${this.baseUrl}put-partner-group`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.put<PartnerGroup>(url, partnerGroup, httpOptions).pipe(
      map((response: any) => {
        // Return the mapped PartnerGroup
        return PartnerGroup.mapProperties(response);
      })
    )
  }

  updatePartnerGroupMember(member: PartnerGroupMember) {
    const url = `${this.baseUrl}put-partner-group-member`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.put<PartnerGroupMember>(url, member, httpOptions).pipe(
      map((response: any) => {
        // Return the mapped PartnerGroupMember
        return PartnerGroup.mapProperties(response);
      }))
  }

  getPartnerGroupsForPartner(partnerId: number) {
    const params = new HttpParams().set('partnerId', partnerId.toString());

    return this.http.get<PartnerGroup[]>(this.baseUrl + 'get-partner-groups', { params }).pipe(
      map((response: any[]) => {
        return response.map(item => PartnerGroup.mapProperties(item))
      })
    )
  }

  addNewPartnerGroupMember(member: PartnerGroupMember, userId: number) {

    const formData = new FormData();
    formData.append('allocationPercentage', member.allocationPercentage.toString());
    formData.append('partnerId', member.partnerId.toString());
    formData.append('partnerGroupId', member.partnerGroupId.toString());
    formData.append('userId', userId.toString());

    if (member.postDilutionParentId) {
      formData.append('postDilutionParentId', member.postDilutionParentId.toString());
    }

    return this.http.post<PartnerGroup>(this.baseUrl + 'post-partner-group-member', formData);
  }

  getPartnerGroupMembers() {

    //pipe not needed currently but leaving
    //in case I add more properties
    return this.http.get<PartnerGroup[]>(this.baseUrl + 'get-partner-groups').pipe(
      map((response: any[]) => {
        return response.map(item => PartnerGroup.mapProperties(item));
      })
    );
  }

  /**
   * use nextUrl for pagination
   * @param nextUrl
   * @returns
   */
  getAllShows(nextUrl?: string, ordering?: string, search?: string) {


    // Construct the URL with query parameters
    if (!nextUrl) {
      let params = new URLSearchParams();

      if (ordering) {
        params.set('ordering', ordering);
      }

      if (search) {
        params.set('search', search);
      }

      nextUrl = `${this.showsUrl}?${params.toString()}`;
    } else {
      // Append query parameters to existing URL
      nextUrl = `${nextUrl}`;
    }

    return this.http.get<Show[]>(nextUrl).pipe(
      map((response: any) => {

        //save next url for more shows
        this.setMoreShowsUrl(response.next);

        return response.results.map(item => Show.mapProperties(item));
      })
    );
  }

  getShow(id: number) {
    const params = new HttpParams().set('id', id.toString());

    return this.http.get<Show[]>(this.showsUrl + 'get-show', { params }).pipe(
      map((response: any) => {
        return Show.mapProperties(response);
      })
    )
  }



  //SC  PRO Shows, seasons, etc

  // partnerGroupId limits the list to shows mapped to that group or with an episode mapped to it
  getAllPROShows(partnerGroupId: number | null = null) {
    const params = partnerGroupId === null ? {} : { partnerGroupId: partnerGroupId.toString() };
    return this.http.get<any>(this.baseUrl + 'get-shows', { params });
  }

  // partnerGroupId narrows the list to that group's shows and the shows with an episode mapped to it
  getPROShowsByPartner(nextUrl?: string, ordering?: string, search?: string, partnerId?: any, partnerGroupId: number | null = null) {
    let params = new HttpParams().set('partnerId', partnerId.toString());
    if (partnerGroupId !== null) {
      params = params.set('partnerGroupId', partnerGroupId.toString());
    }
    return this.http.get<any>(this.baseUrl + 'get-partner-shows', { params });
  }
  // sets only the show's partner group
  updateProShowPartnerGroup(showId: number, partnerGroupId: number) {
    return this.http.patch<any>(this.baseUrl + 'update-show-partner-group/' + showId, { partnerGroup_id: partnerGroupId });
  }

  updateProShow(id?: string, title?: string, description?: string, partnerGroup_id?: number) {
    return this.http.post<any>(this.baseUrl + 'updateshow', { id: id, title: title, description: description, partnerGroup_id: partnerGroup_id });
  }

  bulkUpdateProShows(request: BulkShowRequest): Observable<BulkShowResponse> {
    return this.http.post<BulkShowResponse>(this.baseUrl + 'bulk-updateshow', request);
  }

  updateProSeason(id?: string, showId?: number, seasonNumber?: number) {
    // const params = new HttpParams().set('id', id.toString());

    return this.http.post<any>(this.baseUrl + 'updateseason', { id: id, show: showId, season_number: seasonNumber });
  }

  updateProEpisode(id?: string, seasonNumber?: number, episodeNumber?: number, title?: string, partnerGroupId?: number) {
    return this.http.post<any>(this.baseUrl + 'updatepisode', { id: id, season: seasonNumber, episode_number: episodeNumber, title: title, partnerGroup_id: partnerGroupId });
  }

  deleteProSeason(id: number) {
    return this.http.delete<any>(this.baseUrl + 'delete-season/' + id);
  }

  deleteProEpisode(id: number) {
    return this.http.delete<any>(this.baseUrl + 'delete-episode/' + id);
  }
  //END PRO SHOWS

  getPartnerPros(type: string, year: number, quarter: string, page?: number, pageSize?: number, sortBy?: string, reverseSort?: boolean, partnerId?: number) {
    if (partnerId) {
      const params = new HttpParams().set('type', type)
          .set('year', year.toString())
          .set('quarter', quarter)
          .set('page', (page?.toString() ?? '1'))
          .set('pageSize', (pageSize?.toString() ?? '10'))
          .set('sortBy', sortBy ?? '')
          .set('reverseSort', reverseSort.toString() ?? '')
          .set('partnerId', partnerId.toString() ?? '');
        // return this.http.get<any>(this.baseUrl + 'get-partner-pros', { params });
        return this.http.get<any>(this.baseUrl + 'get-partner-pros-by-partner', { params });
    } else {
      const params = new HttpParams().set('type', type)
      .set('year', year.toString())
      .set('quarter', quarter)
      .set('page', (page?.toString() ?? '1'))
      .set('pageSize', (pageSize?.toString() ?? '10'))
      .set('sortBy', sortBy ?? '')
      .set('reverseSort', reverseSort.toString() ?? '');
      // return this.http.get<any>(this.baseUrl + 'get-partner-pros', { params });
    return this.http.get<any>(this.baseUrl + 'get-partner-pros', { params });

    }


    // .pipe(
    //   map((response: any[]) => {
    //     return response.data.map(item => PartnerPro.mapProperties(item))
    //   })
    // );
  }

  approvePartnerPros(partnerProIds: number[]) {
    const url = `${this.baseUrl}approve-partner-pros`;  // Update with your actual endpoint path
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = { ids: partnerProIds };

    return this.http.put<PartnerPro[]>(url, body, { headers: headers }).pipe(
      map((response: any[]) => {
        return response.map(item => PartnerPro.mapProperties(item))
      })
    );
  }

  getPartnerProsByPartnerId(partnerId: number) {
    const params = new HttpParams().set('partnerId', partnerId.toString())

    //TODO - SET UP API CALL ON BACKEND
    return this.http.get<PartnerPro[]>(this.baseUrl + 'get-partner-pros-by-partner-id', { params }).pipe(
      map((response: any[]) => {
        return response.map(item => PartnerPro.mapProperties(item))
      })
    );
  }


  getUserTypes() {
    return this.http.get<any[]>(this.permissionsUrl + 'dropdown');
  }


  getUserPros() {
    return this.http.get<any[]>(this.userProsUrl + 'get-user-pros');
  }

  getStaffAccounts() {
    return this.http.get<any>(this.usersUrl + 'get-staffs')
  }

  postDataSet(userId: number, proType: string, properties: any[]) {
    const formData = new FormData();
    formData.append('userId', userId.toString())
    formData.append('proType', proType);
    formData.append('properties', JSON.stringify(properties));

    return this.http.post<UserProDataSet>(this.usersUrl + 'dataset/post-data-set/', formData).pipe(
      map((response: any) => {
        // Return the mapped UserProDataSet
        return UserProDataSet.mapProperties(response);
      }));

  }

  getUserDataSets(userId: number) {
    const params = new HttpParams().set('userId', userId.toString());

    return this.http.get<any[]>(this.usersUrl + 'dataset/get-data-sets', { params }).pipe(
      map((response: any) => {
        return response.map(item => UserProDataSet.mapProperties(item));
      })
    );
  }


  /**
   * Returns datasets based on user ids
   */
  getDataSetsByUsers(userIds: string[]) {
    const params = new HttpParams().set('userIds', JSON.stringify(userIds));

    return this.http.get<any[]>(this.usersUrl + 'dataset/get-data-sets-by-user-ids', { params }).pipe(
      map((response: any[]) => {
        return response.map(item => {

          // Return the mapped PartnerPros
          return {
            id: item.id,
            userId: item.user,
            //proType: item.proType
            properties: item.properties
          }
        })
      })
    );
  }


  updateUserProDataSet(dataset: UserProDataSet) {
    const url = `${this.usersUrl}dataset/put-data-set/`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.put<UserProDataSet>(url, dataset, httpOptions).pipe(
      map((response: any) => {
        // Return the mapped UserProDataSet
        return UserProDataSet.mapProperties(response);
      }))
  }

  // data management modal closed event
  notifyModalClosed() {
    this.modalClosedSubject.next();
  }

  createTitleMapping(originalTitle, alternateTitle) {
    const url = `${this.baseUrl}createtitlemapping`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    const body = { 
      original_title: originalTitle,
      alternate_title: alternateTitle
     };
    // const params = new HttpParams().set('original_title', JSON.stringify(originalTitle)).set('alternative_title', JSON.stringify(alternativeTitle));
    return this.http.post<UserProDataSet>(url, body, httpOptions).pipe(
      map((response: any) => {
        return response;
      }))
  }
  private startImport = new Subject<void>();
  startImport$ = this.startImport.asObservable();
  private doneImporting = new Subject<boolean>();
  doneImporting$ = this.doneImporting.asObservable();
  importPartnerGroups(file: File) {
    let formData = new FormData();
    formData.append('file', file);

    this.startImport.next();

    return this.http.post<boolean>(this.baseUrl + 'import-partner-groups', formData).subscribe(res => {
      this.doneImporting.next(res);
    }, ex => {
      this.doneImporting.error(ex);
      console.error('Error importing partner groups:', ex);
    });
  }

}
